export type ThemePresetId =
  | 'custom'
  | 'vercel'
  | 'modern-minimal'
  | 'catppuccin'
  | 'indigo-harbor'
  | 'discord-linen'
  | 'mint-slate'
  | 'lavender-glass'
  | 'nalvra-service'
  | 'matis-labs-amber';

export interface ThemePalette {
  background: string;
  surface: string;
  surfaceMuted: string;
  border: string;
  text: string;
  textMuted: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  accent: string;
  destructive: string;
}

export interface ThemePreset {
  id: Exclude<ThemePresetId, 'custom'>;
  name: string;
  description: string;
  fontLabel: string;
  fontFamily: string;
  radius: string;
  fontSize: '14' | '15' | '16';
  density: 'compact' | 'comfortable';
  rowHeight: 'small' | 'medium' | 'large';
  layout: 'full' | 'wide' | 'boxed';
  presentation: string;
  light: ThemePalette;
  dark: ThemePalette;
  legacy: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
    warning: string;
    error: string;
  };
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'vercel',
    name: 'Vercel',
    description: 'Kontrastreich, klar und konsequent monochrom.',
    fontLabel: 'Geist',
    fontFamily: "'Geist', 'Inter', system-ui, sans-serif",
    radius: '0.5rem',
    fontSize: '14', density: 'compact', rowHeight: 'small', layout: 'full',
    presentation: 'Kompakt · klare Kanten',
    light: { background: '#FAFAFA', surface: '#FFFFFF', surfaceMuted: '#F4F4F5', border: '#E4E4E7', text: '#09090B', textMuted: '#71717A', primary: '#09090B', primaryForeground: '#FAFAFA', secondary: '#E4E4E7', accent: '#D4D4D8', destructive: '#EF4444' },
    dark: { background: '#000000', surface: '#090909', surfaceMuted: '#1F1F1F', border: '#292929', text: '#FAFAFA', textMuted: '#A1A1AA', primary: '#FAFAFA', primaryForeground: '#09090B', secondary: '#262626', accent: '#3F3F46', destructive: '#FF5A5F' },
    legacy: { primary: '#18181B', secondary: '#52525B', accent: '#3F3F46', success: '#22C55E', warning: '#F59E0B', error: '#EF4444' },
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    description: 'Ruhige Flächen und ein präziser blauer Fokus.',
    fontLabel: 'Inter',
    fontFamily: "'Inter', system-ui, sans-serif",
    radius: '0.375rem',
    fontSize: '15', density: 'compact', rowHeight: 'medium', layout: 'wide',
    presentation: 'Reduziert · breite Arbeitsfläche',
    light: { background: '#FFFFFF', surface: '#FFFFFF', surfaceMuted: '#F8FAFC', border: '#E2E8F0', text: '#1E293B', textMuted: '#64748B', primary: '#2563EB', primaryForeground: '#FFFFFF', secondary: '#F1F5F9', accent: '#DBEAFE', destructive: '#EF4444' },
    dark: { background: '#171717', surface: '#262626', surfaceMuted: '#202020', border: '#454545', text: '#F5F5F4', textMuted: '#A8A29E', primary: '#2563EB', primaryForeground: '#FFFFFF', secondary: '#262626', accent: '#294593', destructive: '#EF4444' },
    legacy: { primary: '#2563EB', secondary: '#64748B', accent: '#2563EB', success: '#22C55E', warning: '#F59E0B', error: '#EF4444' },
  },
  {
    id: 'catppuccin',
    name: 'Catppuccin',
    description: 'Sanftes Violett mit Cyan-Akzenten und geringer Härte.',
    fontLabel: 'Montserrat',
    fontFamily: "'Montserrat', 'Trebuchet MS', system-ui, sans-serif",
    radius: '0.35rem',
    fontSize: '16', density: 'comfortable', rowHeight: 'large', layout: 'wide',
    presentation: 'Weich · großzügige Zeilen',
    light: { background: '#EFF1F5', surface: '#FFFFFF', surfaceMuted: '#E6E9EF', border: '#BCC0CC', text: '#4C4F69', textMuted: '#6C6F85', primary: '#8839EF', primaryForeground: '#FFFFFF', secondary: '#CCD0DA', accent: '#04A5E5', destructive: '#D20F39' },
    dark: { background: '#181825', surface: '#1E1E2E', surfaceMuted: '#313244', border: '#45475A', text: '#CDD6F4', textMuted: '#A6ADC8', primary: '#CBA6F7', primaryForeground: '#1E1E2E', secondary: '#585B70', accent: '#89DCEB', destructive: '#F38BA8' },
    legacy: { primary: '#8839EF', secondary: '#6C6F85', accent: '#04A5E5', success: '#40A02B', warning: '#DF8E1D', error: '#D20F39' },
  },
  {
    id: 'indigo-harbor',
    name: 'Indigo Harbor',
    description: 'Seriöses Indigo für professionelle Service-Teams.',
    fontLabel: 'Open Sans',
    fontFamily: "'Open Sans', system-ui, sans-serif",
    radius: '0.5rem',
    fontSize: '15', density: 'comfortable', rowHeight: 'medium', layout: 'full',
    presentation: 'Sachlich · volle Arbeitsfläche',
    light: { background: '#F4F6FD', surface: '#FFFFFF', surfaceMuted: '#F1F3FA', border: '#DEE1EA', text: '#111827', textMuted: '#5B6475', primary: '#23439B', primaryForeground: '#FFFFFF', secondary: '#090909', accent: '#27479D', destructive: '#B0003A' },
    dark: { background: '#030303', surface: '#080808', surfaceMuted: '#171717', border: '#2A2A2A', text: '#F8FAFC', textMuted: '#A7AFBF', primary: '#6B8FD6', primaryForeground: '#08111F', secondary: '#171717', accent: '#4B4B4B', destructive: '#CF7080' },
    legacy: { primary: '#23439B', secondary: '#475569', accent: '#3158B7', success: '#22C55E', warning: '#F59E0B', error: '#B0003A' },
  },
  {
    id: 'discord-linen',
    name: 'Discord Linen',
    description: 'Freundliches Violett mit weichen Flächen und großzügigen Radien.',
    fontLabel: 'Trebuchet MS',
    fontFamily: "'Trebuchet MS', Verdana, sans-serif",
    radius: '0.75rem',
    fontSize: '16', density: 'comfortable', rowHeight: 'large', layout: 'boxed',
    presentation: 'Rund · zentrierte Inhalte',
    light: { background: '#FAFAFB', surface: '#FFFFFF', surfaceMuted: '#F4F4F5', border: '#E4E4E7', text: '#18181B', textMuted: '#71717A', primary: '#5865F2', primaryForeground: '#FFFFFF', secondary: '#D4D4D8', accent: '#818CF8', destructive: '#FF3B30' },
    dark: { background: '#202124', surface: '#232428', surfaceMuted: '#1C1D20', border: '#35363A', text: '#F8F9FA', textMuted: '#AEB1B7', primary: '#5865F2', primaryForeground: '#FFFFFF', secondary: '#DADCE0', accent: '#7C83E8', destructive: '#E63946' },
    legacy: { primary: '#5865F2', secondary: '#71717A', accent: '#818CF8', success: '#22C55E', warning: '#F59E0B', error: '#E63946' },
  },
  {
    id: 'mint-slate',
    name: 'Mint Slate',
    description: 'Technisches Slate mit ruhigem Mint-Fokus und klaren Kanten.',
    fontLabel: 'Consolas / Monospace',
    fontFamily: "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace",
    radius: '0rem',
    fontSize: '14', density: 'compact', rowHeight: 'small', layout: 'full',
    presentation: 'Terminal · dichte Tabellen',
    light: { background: '#F3F7F7', surface: '#FFFFFF', surfaceMuted: '#E8F0F0', border: '#CADADA', text: '#172326', textMuted: '#5F7477', primary: '#4F8588', primaryForeground: '#FFFFFF', secondary: '#E6E1EB', accent: '#E9DDEC', destructive: '#B33A3A' },
    dark: { background: '#0D0E12', surface: '#111218', surfaceMuted: '#1D1E24', border: '#27282E', text: '#E5E7EB', textMuted: '#7B8192', primary: '#8AB3B5', primaryForeground: '#0B1112', secondary: '#25222F', accent: '#3C2D45', destructive: '#8A2020' },
    legacy: { primary: '#4F8588', secondary: '#5F7477', accent: '#745C7D', success: '#3F8F64', warning: '#C58B2A', error: '#B33A3A' },
  },
  {
    id: 'lavender-glass',
    name: 'Lavender Glass',
    description: 'Kantig, ruhig und von kühlen Lavendeltönen getragen.',
    fontLabel: 'Roboto',
    fontFamily: "'Roboto', 'Inter', system-ui, sans-serif",
    radius: '0rem',
    fontSize: '15', density: 'comfortable', rowHeight: 'medium', layout: 'wide',
    presentation: 'Kantig · getönte Flächen',
    light: { background: '#F7F7FC', surface: '#ECEBFF', surfaceMuted: '#E8E7F5', border: '#DDDEE8', text: '#27283B', textMuted: '#686A82', primary: '#7161AE', primaryForeground: '#FFFFFF', secondary: '#E8E6F4', accent: '#DDE5F4', destructive: '#FF1744' },
    dark: { background: '#13131D', surface: '#1E1E2C', surfaceMuted: '#29283A', border: '#3B3A50', text: '#E5E7F5', textMuted: '#A8ABCA', primary: '#A99AE3', primaryForeground: '#171722', secondary: '#37334F', accent: '#3D3D59', destructive: '#D9788A' },
    legacy: { primary: '#7161AE', secondary: '#686A82', accent: '#8F83C7', success: '#22C55E', warning: '#D99027', error: '#D9788A' },
  },
  {
    id: 'nalvra-service',
    name: 'NALVRA SERVICE',
    description: 'Präzise Arbeitsflächen mit zurückhaltenden Goldakzenten.',
    fontLabel: 'Inter',
    fontFamily: "'Inter', system-ui, sans-serif",
    radius: '0.5rem',
    fontSize: '16', density: 'comfortable', rowHeight: 'medium', layout: 'full',
    presentation: 'Ruhig · präzise',
    light: { background: '#F8F8F6', surface: '#FFFFFF', surfaceMuted: '#F3F0E8', border: '#D9DDE0', text: '#0B0D10', textMuted: '#52606B', primary: '#806020', primaryForeground: '#FFFFFF', secondary: '#E8E6DF', accent: '#E3C680', destructive: '#DC2626' },
    dark: { background: '#0B0D10', surface: '#151A20', surfaceMuted: '#1E252C', border: '#303943', text: '#F3F0E8', textMuted: '#B8C0C8', primary: '#E3C680', primaryForeground: '#0B0D10', secondary: '#303943', accent: '#C8A45D', destructive: '#EF4444' },
    legacy: { primary: '#806020', secondary: '#303943', accent: '#C8A45D', success: '#22C55E', warning: '#F59E0B', error: '#EF4444' },
  },
  {
    id: 'matis-labs-amber',
    name: 'Matis Labs Amber',
    description: 'Warme Amber-Akzente mit natürlicher, hochwertiger Anmutung.',
    fontLabel: 'Outfit',
    fontFamily: "'Outfit', 'Segoe UI', system-ui, sans-serif",
    radius: '1rem',
    fontSize: '16', density: 'comfortable', rowHeight: 'large', layout: 'boxed',
    presentation: 'Warm · großzügige Karten',
    light: { background: '#FAF9F6', surface: '#F7F6F2', surfaceMuted: '#EEEADF', border: '#DDDBD4', text: '#151513', textMuted: '#68665F', primary: '#C15330', primaryForeground: '#FFFFFF', secondary: '#EAE7DD', accent: '#E9E6DC', destructive: '#151513' },
    dark: { background: '#22231F', surface: '#2A2B28', surfaceMuted: '#1E1F1C', border: '#45453E', text: '#F7F7F2', textMuted: '#B9B8AE', primary: '#E07B57', primaryForeground: '#171713', secondary: '#FAF9F5', accent: '#1D1D19', destructive: '#FA4147' },
    legacy: { primary: '#C15330', secondary: '#68665F', accent: '#B45D3D', success: '#3F8F64', warning: '#D99027', error: '#D14343' },
  },
];

export function findThemePreset(id?: string | null): ThemePreset | undefined {
  return THEME_PRESETS.find((preset) => preset.id === id);
}

export function themePresetUpdates(preset: ThemePreset) {
  return {
    theme_preset: preset.id,
    primary_color: preset.legacy.primary,
    secondary_color: preset.legacy.secondary,
    accent_color: preset.legacy.accent,
    success_color: preset.legacy.success,
    warning_color: preset.legacy.warning,
    error_color: preset.legacy.error,
    font_family: preset.fontFamily,
    base_font_size: preset.fontSize,
    density: preset.density,
    table_row_height: preset.rowHeight,
    content_layout: preset.layout,
    line_height: preset.density === 'compact' ? 'compact' as const : 'normal' as const,
    google_font_url: '',
    google_font_name: '',
  } as const;
}
